import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import { usePatchTask, useTasks, useTeam } from '../lib/api/tasks';
import { useCampaigns } from '../lib/api/campaigns';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import TaskRow from '../components/tasks/TaskRow';
import CreateTaskModal from '../components/modals/CreateTaskModal';
import { useTaskActions } from '../components/tasks/useTaskActions';
import { BUCKET_LABEL, BUCKET_ORDER, groupTasks } from '../lib/taskBuckets';
import { OwnerAvatar } from '../lib/brandAvatars';
import { filterTasks } from '../lib/taskSearch';
import TaskSearchBox from '../components/tasks/TaskSearchBox';

type FilterMode = 'assigned' | 'created' | 'both';

const MyWork = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filterMode, setFilterMode] = useState<FilterMode>('assigned');
  const [showCompleted, setShowCompleted] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { data: team = [] } = useTeam();
  const myTeamUser = user ? team.find((u) => u.user_id === user.user_id) : null;

  // Issue two queries depending on the filter mode, then merge on the client for the "both" case.
  const assignedQuery = useTasks(
    filterMode === 'assigned' || filterMode === 'both' ? { assignee: 'me' } : undefined
  );
  const createdQuery = useTasks(
    filterMode === 'created' || filterMode === 'both' ? { created_by: 'me' } : undefined
  );

  const tasks = useMemo(() => {
    if (filterMode === 'assigned') return assignedQuery.data ?? [];
    if (filterMode === 'created') return createdQuery.data ?? [];
    // both: merge + dedupe by id
    const seen = new Set<string>();
    const out = [] as NonNullable<typeof assignedQuery.data>;
    for (const t of [...(assignedQuery.data ?? []), ...(createdQuery.data ?? [])]) {
      if (!seen.has(t.id)) {
        seen.add(t.id);
        out.push(t);
      }
    }
    return out;
  }, [filterMode, assignedQuery.data, createdQuery.data]);

  const isLoading =
    (filterMode !== 'created' && assignedQuery.isLoading) ||
    (filterMode !== 'assigned' && createdQuery.isLoading);

  const { data: campaigns = [] } = useCampaigns();
  const patchTask = usePatchTask();
  const taskActions = useTaskActions();
  const campaignById = new Map(campaigns.map((c) => [c.id, c]));

  const shownTasks = useMemo(
    () => filterTasks(tasks, query, (id) => (id ? campaignById.get(id)?.name : undefined), team),
    // campaignById is rebuilt every render from `campaigns`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, query, campaigns, team],
  );
  const buckets = useMemo(() => groupTasks(shownTasks, showCompleted), [shownTasks, showCompleted]);
  const overdue = buckets.overdue.length;
  const thisWeek = buckets.today.length + buckets.thisWeek.length;
  const myCampaigns = new Set(
    tasks.map((t) => t.campaign).filter((c): c is string => !!c),
  );

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {myTeamUser && <OwnerAvatar user={myTeamUser} size={48} />}
          <div>
            <div className="page-eyebrow">Your queue</div>
            <h1 className="page-title">
              {user?.displayName ? user.displayName : 'My work'}
            </h1>
            <p className="page-sub">
              {tasks.length} task{tasks.length === 1 ? '' : 's'}
              {filterMode === 'assigned' ? ' assigned to you' : filterMode === 'created' ? ' you created' : ' involving you'}
              {overdue > 0 ? ` · ${overdue} overdue` : ''}.
            </p>
          </div>
        </div>
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          <Icon name="plus" size={13} /> New task
        </Button>
      </div>

      <div className="kpi-grid">
        <div className="kpi">
          <div className="kpi-label">Open tasks</div>
          <div className="kpi-value">{tasks.filter((t) => t.status !== 'Done').length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Overdue</div>
          <div className="kpi-value" style={{ color: overdue > 0 ? 'var(--warn)' : undefined }}>
            {overdue}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Due this week</div>
          <div className="kpi-value">{thisWeek}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Campaigns you're on</div>
          <div className="kpi-value">{myCampaigns.size}</div>
        </div>
      </div>

      <div className="filter-bar" style={{ flexWrap: 'wrap' }}>
        <TaskSearchBox value={query} onChange={setQuery} />
        <button
          type="button"
          className={`filter-pill ${filterMode === 'assigned' ? 'active' : ''}`}
          onClick={() => setFilterMode('assigned')}
        >
          Assigned to me
        </button>
        <button
          type="button"
          className={`filter-pill ${filterMode === 'created' ? 'active' : ''}`}
          onClick={() => setFilterMode('created')}
        >
          Created by me
        </button>
        <button
          type="button"
          className={`filter-pill ${filterMode === 'both' ? 'active' : ''}`}
          onClick={() => setFilterMode('both')}
        >
          Both
        </button>
        <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 6px' }} />
        <button
          type="button"
          className={`filter-pill ${showCompleted ? 'active' : ''}`}
          onClick={() => setShowCompleted((v) => !v)}
        >
          Show completed
        </button>
      </div>

      {isLoading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
          Loading…
        </div>
      ) : tasks.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <p style={{ color: 'var(--ink-3)', marginBottom: 12 }}>
            {filterMode === 'assigned'
              ? 'Nothing assigned to you yet.'
              : filterMode === 'created'
              ? "You haven't created any tasks yet."
              : 'No tasks involve you yet.'}
          </p>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={13} /> Create a task
          </Button>
        </div>
      ) : shownTasks.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)' }}>
          No tasks match “{query.trim()}”.
        </div>
      ) : (
        BUCKET_ORDER.map((key) => {
          const ts = buckets[key];
          if (ts.length === 0) return null;
          return (
            <div key={key} className="section">
              <div className="section-head">
                <div>
                  <div className="section-title">{BUCKET_LABEL[key]}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                    {ts.length} task{ts.length === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
              <div className="card">
                {ts.map((t) => {
                  const c = t.campaign ? campaignById.get(t.campaign) : undefined;
                  return (
                    <TaskRow
                      key={t.id}
                      task={t}
                      team={team}
                      campaignName={c?.name}
                      campaignType={c?.type}
                      onCampaignClick={(cid) => navigate(`/campaigns/${cid}`)}
                      onToggle={() =>
                        patchTask.mutate({
                          id: t.id,
                          patch: { status: t.status === 'Done' ? 'In progress' : 'Done' },
                        })
                      }
                      onAssign={(ids) => patchTask.mutate({ id: t.id, patch: { assignees: ids } })}
                      onEdit={taskActions.openEdit}
                      onDelete={taskActions.openDelete}
                    />
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      <CreateTaskModal open={createOpen} onClose={() => setCreateOpen(false)} />
      {taskActions.modals}
    </div>
  );
};

export default MyWork;
